import { useState } from 'react';
import { line, JOB_TYPES_PATH, Size, jobTypeLengthText, t, workOrderJobTypePath, type JobTypesAnswer } from '@bms2/shared';
import { Button, Select } from '../../components/controls';
import { FormFrame } from '../../components/forms';
import { post, useApi } from './api';
import styles from './RecordPage.module.css';

/** What the job's own page reads about its type: the one it was pre-filled from, read by name even once deactivated. */
export interface WorkOrderJobTypeFacts {
  id: string;
  workOrderNumber: string;
  status: string;
  editable: boolean;
  version?: number;
  jobType?: { id: string; name: string; isActive: boolean; estimatedMinutes: number | null } | null;
}

/**
 * 🔒 Piece 29 (T1710): the job type a Work Order was pre-filled from, and the
 * pick on a Draft. Picking a type writes its labor lines with their levels and
 * sub-skills, its planned parts and its checklist in one save. 🔨 A type is
 * applied once: on a job already naming one, the window says before it saves
 * that the new type is recorded and the lines, parts and checklist stand.
 */
export function WorkOrderJobType({ document, apiBaseUrl = '', onChanged }: { document: WorkOrderJobTypeFacts; apiBaseUrl?: string; onChanged: () => void }) {
  const [picking, setPicking] = useState(false);
  const current = document.jobType ?? null;
  const offers = document.status === 'Draft' && document.editable;
  return (
    <div className={styles.actions} data-work-order-job-type={current?.name ?? 'none'}>
      <p className={styles.hint}>
        {line(t('workOrder.jobType.label'), '·', current ? `${current.name}${current.isActive ? '' : ` (${t('workOrder.jobType.inactive')})`}` : t('workOrder.jobType.none'))}
      </p>
      {offers ? <Button size={Size.Small} text={[current ? t('workOrder.jobType.change') : t('workOrder.jobType.pick')]} actions={[() => setPicking(true)]} /> : null}
      {/* 🔨 Mounted only while open, so each opening reads the types afresh (piece 13). */}
      {picking ? <JobTypePickWindow document={document} apiBaseUrl={apiBaseUrl} onClose={() => setPicking(false)} onPicked={() => { setPicking(false); onChanged(); }} /> : null}
    </div>
  );
}

function JobTypePickWindow({ document, apiBaseUrl, onClose, onPicked }: { document: WorkOrderJobTypeFacts; apiBaseUrl: string; onClose: () => void; onPicked: () => void }) {
  const read = useApi<JobTypesAnswer>(JOB_TYPES_PATH, apiBaseUrl);
  const [chosen, setChosen] = useState('');
  /** 🔒 Decision 3063: a refused pick reads inside the window, which stays open for the correction. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  // 🔒 A deactivated type is offered on no new job, and the job's own type is not offered back to it.
  const offered = (answer?.rows ?? []).filter((row) => row.isActive && row.id !== document.jobType?.id);
  const type = offered.find((row) => row.id === chosen) ?? null;
  const pick = async () => {
    const outcome = await post(workOrderJobTypePath(document.id), { jobTypeId: chosen, version: document.version ?? null }, apiBaseUrl);
    if (!outcome.ok) return setRefusal(outcome.message);
    onPicked();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('workOrder.jobType.pickTitle')}
      subtitle={document.workOrderNumber}
      dataForm="work-order-job-type"
      typed={chosen !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('workOrder.jobType.act'), disabled: chosen === '', onAct: () => pick() }}
    >
      {/* 🔨 Piece 29: the form says before it saves what a second pick does, which is to record the type and nothing else. */}
      <p className={styles.hint} data-job-type-applied-once={document.jobType ? 'true' : 'false'}>{document.jobType ? t('workOrder.jobType.appliedOnce') : t('workOrder.jobType.fills')}</p>
      {answer && offered.length === 0 ? <p className={styles.hint} data-job-type-none-made="true">{t('workOrder.jobType.noneMade')}</p> : null}
      <Select size={Size.Large}
        label={t('workOrder.jobType.choice')}
        value={chosen}
        onChange={setChosen}
        placeholder={t('workOrder.new.noneYet')}
        options={offered.map((row) => ({ value: row.id, label: row.name }))}
        dataAttribute={{ 'data-job-type-choice': 'true' }}
        required
      />
      {type ? (
        <p className={styles.hint} data-job-type-preview={type.name}>
          {line(
            jobTypeLengthText(type.estimatedMinutes) ?? t('settings.jobTypes.noLength'), '·',
            `${type.tasks.length} ${type.tasks.length === 1 ? t('settings.jobTypes.taskOne') : t('settings.jobTypes.taskCount')}`, '·',
            `${type.parts.length} ${type.parts.length === 1 ? t('settings.jobTypes.partOne') : t('settings.jobTypes.partCount')}`, '·',
            type.checklistTemplateName ?? t('settings.jobTypes.noTemplate'),
          )}
        </p>
      ) : null}
    </FormFrame>
  );
}
